import { z } from "zod";
// GA-4 — « Papillon » : noms officiels, paquet et barème vivent dans UN seul
// fichier (voir son en-tête juridique), ré-exporté en bas de celui-ci.
import { PAPILLON_MAX_VALUE, PAPILLON_MIN_VALUE, PapillonCardKindSchema, PapillonOpSchema, } from "./papillonCards.js";
// ---------------------------------------------------------------------------
// Piste Jeux (G1) — schémas partagés client/serveur
// L'état des parties est autoritaire côté serveur : le client ne reçoit que
// des états "redacted" (le mot du Pendu ne quitte jamais le serveur).
// ---------------------------------------------------------------------------
export const GameTypeSchema = z.enum([
    "PENDU",
    "SNAKE",
    "PICTIO",
    "TRIVIA",
    // L3-6 — « Pong » au sens générique du genre paddle (aucune marque déposée).
    "PONG",
    // L3-7 — « Mot Mystère » : mécanique Motus/Wordle SANS la marque.
    "MYSTERE",
    // Roadmap #2 — Morpion (tic-tac-toe, mécanique universelle).
    "MORPION",
    // « Les Boîtes » — mécanique boîtes & offres, nom et présentateur maison
    // (aucun format télé, aucune personne réelle).
    "BOITES",
    // Roadmap #3 — « Aligne 4 » : mécanique puissance-4 SANS la marque Hasbro
    // (nom maison, visuels 100 % LiveMessage).
    "ALIGNE4",
    // Roadmap #4 — « Bataille navale » : battleship, jeu traditionnel du
    // domaine public. La redaction par joueur est le cœur du jeu.
    "BATAILLE",
    // GA-1 — « Casse-briques » : mécanique breakout générique, SOLO uniquement
    // (nom maison, aucun graphisme ni marque d'un jeu existant).
    "BRIQUES",
    // GA-2 — « Petit Bac » : jeu traditionnel du domaine public (le petit
    // baccalauréat), premier jeu à MANCHES du produit.
    "PETITBAC",
    // GA-4 — « Papillon », manche bleue : jeu de cartes SOUS LICENCE de
    // l'éditeur (détenue par Henri) — d'où les noms officiels, exception à la
    // règle juridique de docs/GAMES_ROADMAP.md. Aucun asset de l'éditeur.
    "PAPILLON",
]);
export const GAME_META = {
    PENDU: { label: "Pendu", solo: true, realtime: false, icon: "pendu", clientReady: true, order: 1 },
    SNAKE: { label: "Snake", solo: true, realtime: true, icon: "snake", clientReady: true, order: 2 },
    PONG: { label: "Pong", solo: true, realtime: true, icon: "pong", clientReady: true, order: 3 },
    TRIVIA: { label: "Trivia", solo: true, realtime: false, icon: "trivia", clientReady: true, order: 4 },
    MYSTERE: { label: "Mot Mystère", solo: true, realtime: false, icon: "mystere", clientReady: true, order: 5 },
    PICTIO: { label: "Pictio", solo: false, realtime: true, icon: "pictio", clientReady: true, order: 6 },
    MORPION: { label: "Morpion", solo: false, realtime: false, icon: "morpion", clientReady: true, order: 7 },
    ALIGNE4: { label: "Aligne 4", solo: false, realtime: false, icon: "aligne4", clientReady: true, order: 8 },
    BATAILLE: { label: "Bataille navale", solo: false, realtime: false, icon: "bataille", clientReady: true, order: 9 },
    BOITES: { label: "Les Boîtes", solo: true, realtime: false, icon: "boites", clientReady: true, order: 10 },
    BRIQUES: { label: "Casse-briques", solo: true, realtime: true, icon: "briques", clientReady: true, order: 11, soloOnly: true },
    // GA-2 — duel obligatoire : sans adversaire il n'y a ni doublon ni suspense,
    // le barème dégénère (tout serait unique, donc 2 points).
    PETITBAC: { label: "Petit Bac", solo: false, realtime: false, icon: "petitbac", clientReady: true, order: 12 },
    // GA-4 — le plateau, le reducer et les tests sont livrés, mais le jeu ne
    // s'expose PAS encore, tant que la PORTÉE ÉCRITE de la licence (adaptation
    // numérique + distribution sur les stores) n'est pas confirmée par l'éditeur.
    // DEUX interrupteurs, et le second seul fait foi :
    //   1. `clientReady: false` ici — retire la tuile du hub, la chip de la sheet
    //      Défi et les chips du lien de défi. AFFICHAGE SEULEMENT.
    //   2. `PAPILLON_ENABLED=false` côté API (défaut) — LA garde : aucune partie
    //      ne peut être créée ni jouée, par aucun chemin. C'est elle qu'il faut
    //      lever le jour J, EN MÊME TEMPS que celle-ci.
    PAPILLON: { label: "Papillon", solo: true, realtime: false, icon: "papillon", clientReady: true, order: 13 },
};
/** Nom affiché d'un jeu — tolère un type inconnu (client N-1 face à un enum
 *  serveur plus récent) en le renvoyant tel quel. */
export function gameLabel(gameType) {
    return GAME_META[gameType]?.label ?? gameType;
}
/** Jeux proposables à l'écran (tuiles du hub, sheet Défi, chips de lien),
 *  dans l'ordre d'affichage canonique. Départage par le nom du type quand deux
 *  jeux portent le même `order` : plusieurs lots parallèles ajoutent leur jeu
 *  en fin de registre et se retrouvent au même numéro au merge — sans clé de
 *  départage, l'ordre des tuiles du hub deviendrait arbitraire (et instable
 *  d'un moteur JS à l'autre). */
export const CLIENT_READY_GAMES = GameTypeSchema.options
    .filter((t) => GAME_META[t].clientReady)
    .sort((a, b) => GAME_META[a].order - GAME_META[b].order || a.localeCompare(b));
/** GA-1 — jeux proposables EN DÉFI (sheet Défi, chips de lien de défi) : les
 *  jeux `soloOnly` en sont exclus, sinon le client proposerait un duel que le
 *  serveur refuse (400 SOLO_ONLY_GAME). Les tuiles du hub, elles, continuent
 *  d'utiliser CLIENT_READY_GAMES : le casse-briques y a bien sa place. */
export const DUEL_READY_GAMES = CLIENT_READY_GAMES.filter((t) => GAME_META[t].soloOnly !== true);
export const GameMatchStatusSchema = z.enum([
    "PENDING",
    "ACTIVE",
    "FINISHED",
    "CANCELLED",
    "EXPIRED",
]);
// ---------------------------------------------------------------------------
// WebSocket — client → serveur
// ---------------------------------------------------------------------------
export const JoinMatchEventSchema = z.object({
    type: z.literal("join_match"),
    matchId: z.string().uuid(),
});
/** L'action est opaque ici — re-validée par le schéma Zod du jeu côté moteur. */
export const GameActionEventSchema = z.object({
    type: z.literal("game_action"),
    matchId: z.string().uuid(),
    action: z.unknown(),
});
// ---------------------------------------------------------------------------
// WebSocket — serveur → client
// ---------------------------------------------------------------------------
/** Événement de partie (state_sync, coup joué, erreur…) — payload par jeu.
 *  gameType absent sur les erreurs génériques (BUSY, NO_STATE…). */
export const GameEventSchema = z.object({
    type: z.literal("game_event"),
    matchId: z.string().min(1),
    gameType: GameTypeSchema.optional(),
    event: z.unknown(),
});
/** Cycle de vie du match (défi créé / accepté / refusé / terminé) — fait
 *  refetch la carte Défi et les listes. */
export const GameMatchUpdatedEventSchema = z.object({
    type: z.literal("game_match_updated"),
    matchId: z.string().min(1),
    gameType: GameTypeSchema,
    status: GameMatchStatusSchema,
    sessionId: z.string().nullable().optional(),
    groupId: z.string().nullable().optional(),
});
// ---------------------------------------------------------------------------
// Pendu — actions (client → moteur) et état public (moteur → clients)
// ---------------------------------------------------------------------------
/**
 * Modes du Pendu :
 * - "duel"   : mot tiré par le serveur, les deux joueurs devinent en alternance ;
 * - "chosen" : le challenger CHOISIT le mot (suggestion ou saisie libre) et
 *              regarde l'autre jouer — s'il est pendu, le choisisseur gagne ;
 * - "solo"   : mot du serveur, un seul joueur (entraînement, points réduits).
 */
export const PenduModeSchema = z.enum(["duel", "chosen", "solo"]);
/** Mot choisi : lettres FR (accents), tiret/espace/apostrophe internes, 2-24. */
export const PENDU_WORD_REGEX = /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ' -]{0,22}[A-Za-zÀ-ÿ]$/u;
export const PenduActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("guess_letter"),
        letter: z
            .string()
            .length(1)
            .regex(/^[A-Za-zÀ-ÿ]$/u),
    }),
    z.object({
        kind: z.literal("guess_word"),
        word: z.string().min(2).max(40),
    }),
]);
export const PENDU_MAX_ERRORS = 8;
/** État redacted diffusé aux joueurs — jamais le mot tant que la partie dure. */
export const PenduPublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    /** Motif du mot, lettres non trouvées masquées : "P _ N D _" */
    masked: z.string(),
    guessedLetters: z.array(z.string()),
    wrongLetters: z.array(z.string()),
    errors: z.number().int().min(0),
    maxErrors: z.number().int(),
    currentTurnUserId: z.string(),
    /** Joueurs éliminés (proposition de mot fausse). */
    eliminatedUserIds: z.array(z.string()),
    winnerUserId: z.string().nullable(),
    /** Mode "chosen" : celui qui a choisi le mot (spectateur). */
    chooserUserId: z.string().nullable(),
    /** Révélé quand phase === "finished" — et en continu pour le choisisseur. */
    word: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Snake (L3-1) — premier jeu TEMPS RÉEL à tick serveur. Contrairement au
// Pendu (asynchrone), les deux joueurs doivent être présents : la partie
// attend tout le monde (phase "waiting"), compte à rebours, puis le serveur
// avance les serpents à chaque tick. Déconnexion > 15 s = forfait.
// ---------------------------------------------------------------------------
export const SnakeDirSchema = z.enum(["up", "down", "left", "right"]);
export const SnakeActionSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("turn"), dir: SnakeDirSchema }),
]);
export const SNAKE_GRID_W = 20;
export const SNAKE_GRID_H = 20;
export const SNAKE_TICK_MS = 200;
/** Déconnecté plus de 15 s pendant une partie à tick → forfait. */
export const REALTIME_DISCONNECT_FORFEIT_MS = 15_000;
/** Cellule [x, y] — origine en haut à gauche. */
export const SnakeCellSchema = z.tuple([z.number().int(), z.number().int()]);
export const SnakePublicSnakeSchema = z.object({
    userId: z.string(),
    body: z.array(SnakeCellSchema),
    dir: SnakeDirSchema,
    alive: z.boolean(),
    /** Pommes mangées. */
    score: z.number().int(),
});
export const SnakeEndReasonSchema = z.enum([
    "crash",
    "forfeit",
    "timeout",
    "waiting_timeout",
]);
/** État public du Snake — aucune donnée secrète, identique pour tous. */
export const SnakePublicStateSchema = z.object({
    phase: z.enum(["waiting", "countdown", "playing", "finished"]),
    gridW: z.number().int(),
    gridH: z.number().int(),
    snakes: z.array(SnakePublicSnakeSchema),
    food: z.array(SnakeCellSchema),
    /** Phase "countdown" : millisecondes avant le départ. */
    countdownMsLeft: z.number().int().nullable(),
    /** Phase "playing" : temps de partie restant (limite anti-stalling). */
    timeLeftMs: z.number().int().nullable(),
    /** Joueurs actuellement présents (plateau ouvert) — pour « en attente de X ». */
    presentUserIds: z.array(z.string()),
    winnerUserId: z.string().nullable(),
    endReason: SnakeEndReasonSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Pong (L3-6) — deuxième jeu à tick : physique raquettes/balle côté serveur,
// mêmes règles de présence que le Snake (waiting/countdown, forfait 15 s).
// ---------------------------------------------------------------------------
export const PONG_FIELD_W = 100;
export const PONG_FIELD_H = 62;
export const PONG_TICK_MS = 120;
export const PONG_PADDLE_HALF_H = 7;
export const PONG_WIN_SCORE = 5;
export const PongActionSchema = z.discriminatedUnion("kind", [
    /** Contrôle continu (clavier) : la raquette glisse tant que dir ≠ stop. */
    z.object({
        kind: z.literal("paddle"),
        dir: z.enum(["up", "down", "stop"]),
    }),
    /** Contrôle tactile : viser une hauteur — la vitesse reste plafonnée
     *  côté serveur (pas de téléportation). */
    z.object({
        kind: z.literal("paddle_to"),
        y: z.number().min(0).max(PONG_FIELD_H),
    }),
]);
export const PongPublicPaddleSchema = z.object({
    userId: z.string(),
    /** Centre de la raquette (unités terrain). */
    y: z.number(),
    side: z.enum(["left", "right"]),
    score: z.number().int(),
});
export const PongEndReasonSchema = z.enum([
    "score",
    "forfeit",
    "waiting_timeout",
]);
export const PongPublicStateSchema = z.object({
    phase: z.enum(["waiting", "countdown", "playing", "finished"]),
    fieldW: z.number().int(),
    fieldH: z.number().int(),
    paddles: z.array(PongPublicPaddleSchema),
    ball: z.object({
        x: z.number(),
        y: z.number(),
        vx: z.number(),
        vy: z.number(),
    }),
    /** Pause de service après un point (ms restantes), null sinon. */
    serveMsLeft: z.number().int().nullable(),
    countdownMsLeft: z.number().int().nullable(),
    presentUserIds: z.array(z.string()),
    winScore: z.number().int(),
    winnerUserId: z.string().nullable(),
    endReason: PongEndReasonSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Trivia (L3-2) — quiz éclair ASYNCHRONE (comme le Pendu) : chacun répond aux
// mêmes questions à son rythme, push « à toi de jouer » quand l'un finit.
// Les questions (et surtout les bonnes réponses) vivent côté serveur : le
// client ne reçoit la correction qu'APRÈS avoir répondu.
// ---------------------------------------------------------------------------
export const TRIVIA_ROUNDS = 5;
export const TRIVIA_CHOICES = 4;
export const TriviaActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("answer"),
        /** Index de la question (doit être MA question courante — anti-rejeu). */
        index: z.number().int().min(0),
        choice: z.number().int().min(0).max(TRIVIA_CHOICES - 1),
    }),
]);
/** Question posée — jamais la bonne réponse tant qu'on n'a pas répondu. */
export const TriviaPublicQuestionSchema = z.object({
    q: z.string(),
    choices: z.array(z.string()),
    category: z.string().optional(),
});
/** Mes réponses passées, corrigées (le serveur révèle après coup). */
export const TriviaAnsweredSchema = z.object({
    q: z.string(),
    myChoice: z.number().int(),
    correctChoice: z.number().int(),
    correct: z.boolean(),
});
export const TriviaPublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    rounds: z.number().int(),
    /** Ma progression et ma question courante (null si j'ai fini). */
    myIndex: z.number().int(),
    question: TriviaPublicQuestionSchema.nullable(),
    myAnswers: z.array(TriviaAnsweredSchema),
    /** Progression des joueurs — les scores ne sont révélés qu'à la fin. */
    players: z.array(z.object({
        userId: z.string(),
        done: z.number().int(),
        /** Bonnes réponses — null tant que la partie n'est pas finie. */
        score: z.number().int().nullable(),
    })),
    winnerUserId: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Pictio (G3b) — dessine & devine : la signature du produit (on voit l'autre
// écrire en direct) appliquée au dessin. Le dessinateur trace, l'adversaire
// voit le trait SE DESSINER : chaque lot de points part en `game_action` et
// revient par le state_sync. Duel en manches alternées, mot serveur, chrono
// de manche côté serveur. Le devineur ne reçoit JAMAIS le mot (redaction).
// ---------------------------------------------------------------------------
/** Manches (alternées) : chacun dessine PICTIO_ROUNDS / 2 fois. */
export const PICTIO_ROUNDS = 4;
export const PICTIO_ROUND_MS = 90_000;
export const PICTIO_REVEAL_MS = 4_000;
/** Le canvas est un carré normalisé — coordonnées entières 0..1000. */
export const PICTIO_CANVAS = 1000;
/** Palette : 4 encres (index 0-3) + gomme (flag `eraser`). */
export const PICTIO_COLOR_COUNT = 4;
/** Bornes anti-abus : un lot ≤ 64 points, ≤ 256 traits et ≤ 8000 points par
 *  manche (au-delà, le reducer rejette — le dessin reste largement possible). */
export const PICTIO_MAX_PTS_PER_ACTION = 64;
export const PICTIO_MAX_STROKES = 256;
export const PICTIO_MAX_POINTS_TOTAL = 8_000;
/** Points compressés : [x0, y0, x1, y1, …] en unités canvas (0..1000). */
const PictioPtsSchema = z
    .array(z.number().int().min(0).max(PICTIO_CANVAS))
    .min(2)
    .max(PICTIO_MAX_PTS_PER_ACTION * 2)
    .refine((pts) => pts.length % 2 === 0, { message: "pts must be x,y pairs" });
export const PictioActionSchema = z.discriminatedUnion("kind", [
    /** Lot de points du trait `id` — crée le trait ou l'étend (dessinateur). */
    z.object({
        kind: z.literal("stroke"),
        id: z.number().int().min(0).max(100_000),
        color: z.number().int().min(0).max(PICTIO_COLOR_COUNT - 1),
        eraser: z.boolean().optional(),
        pts: PictioPtsSchema,
    }),
    /** Efface tout le canvas de la manche (dessinateur). */
    z.object({ kind: z.literal("clear") }),
    /** Proposition en texte libre (devineur) — accents/casse insensibles. */
    z.object({ kind: z.literal("guess"), word: z.string().trim().min(1).max(40) }),
    /** Le dessinateur passe (mot impossible) : 0 point, manche suivante. */
    z.object({ kind: z.literal("pass") }),
]);
export const PictioStrokeSchema = z.object({
    id: z.number().int(),
    color: z.number().int(),
    eraser: z.boolean(),
    pts: z.array(z.number().int()),
});
export const PictioGuessSchema = z.object({
    userId: z.string(),
    text: z.string(),
    correct: z.boolean(),
});
export const PictioRoundResultSchema = z.object({
    drawerUserId: z.string(),
    /** Révélé une fois la manche terminée seulement. */
    word: z.string(),
    outcome: z.enum(["found", "timeout", "pass"]),
});
export const PictioEndReasonSchema = z.enum([
    "score",
    "forfeit",
    "waiting_timeout",
]);
/** État redacted : le mot courant n'y figure QUE pour le dessinateur. */
export const PictioPublicStateSchema = z.object({
    phase: z.enum(["waiting", "countdown", "drawing", "reveal", "finished"]),
    roundIdx: z.number().int(),
    totalRounds: z.number().int(),
    drawerUserId: z.string(),
    /** Le mot à dessiner — null pour le devineur (toujours). */
    word: z.string().nullable(),
    /** Longueur du mot (indice affiché au devineur). */
    wordLen: z.number().int(),
    strokes: z.array(PictioStrokeSchema),
    guesses: z.array(PictioGuessSchema),
    roundMsLeft: z.number().int().nullable(),
    revealMsLeft: z.number().int().nullable(),
    countdownMsLeft: z.number().int().nullable(),
    /** Manches terminées — mots révélés. */
    results: z.array(PictioRoundResultSchema),
    scores: z.array(z.object({ userId: z.string(), score: z.number().int() })),
    presentUserIds: z.array(z.string()),
    winnerUserId: z.string().nullable(),
    endReason: PictioEndReasonSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Pictio — deltas incrémentaux (US2). Rebroadcaster l'état COMPLET à chaque
// lot de traits ET à chaque tick pouvait atteindre ~1 Mo/s par joueur en fin
// de manche (8000 points × 4-5 syncs/s). Le serveur n'envoie plus que :
// - `pictio_stroke` : les NOUVEAUX points d'un trait (append-only, comme le
//   rendu client StrokePolyline) ;
// - `pictio_clock` : le tick d'horloge (chrono, présence) sans les traits.
// Les transitions de phase, guesses, clear et fins de manche restent des
// state_sync complets ; le join_match/reconnexion resynchronise tout.
// Aucun secret : les traits sont publics (c'est le plateau).
// ---------------------------------------------------------------------------
export const PictioStrokeDeltaSchema = z.object({
    kind: z.literal("pictio_stroke"),
    /** Garde anti-obsolescence : un delta d'une manche passée est ignoré. */
    roundIdx: z.number().int(),
    id: z.number().int(),
    color: z.number().int(),
    eraser: z.boolean(),
    /** Les points AJOUTÉS (pas le trait complet) — le client les append. */
    pts: z.array(z.number().int()),
});
export const PictioClockDeltaSchema = z.object({
    kind: z.literal("pictio_clock"),
    roundIdx: z.number().int(),
    /** Toujours la phase COURANTE (jamais une transition — celles-ci passent
     *  par un state_sync complet) : le client ignore le delta si elle diffère. */
    phase: z.enum(["waiting", "countdown", "drawing", "reveal"]),
    roundMsLeft: z.number().int().nullable(),
    revealMsLeft: z.number().int().nullable(),
    countdownMsLeft: z.number().int().nullable(),
    presentUserIds: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Mot Mystère (L3-7) — deviner un mot FR (5-7 lettres) en 6 essais, feedback
// lettre bien placée / présente / absente. ASYNCHRONE comme le Pendu : même
// mot pour les deux, chacun sa grille à son rythme — celui qui trouve en
// moins d'essais gagne. Redaction stricte : on ne voit pas les essais
// adverses avant d'avoir fini (juste leur nombre), jamais le mot avant la fin.
// ---------------------------------------------------------------------------
export const MYSTERE_MAX_TRIES = 6;
export const MYSTERE_MIN_LEN = 5;
export const MYSTERE_MAX_LEN = 7;
export const MystereActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("guess"),
        /** Le mot proposé — accents/casse insensibles, marge pour les accents. */
        word: z.string().trim().min(MYSTERE_MIN_LEN).max(MYSTERE_MAX_LEN + 3),
    }),
]);
/** Feedback d'une case : 0 absente, 1 présente mal placée, 2 bien placée. */
export const MystereCellSchema = z.union([
    z.literal(0),
    z.literal(1),
    z.literal(2),
]);
export const MystereRowSchema = z.object({
    /** Lettres de l'essai — null en silhouette (grille adverse). */
    letters: z.array(z.string()).nullable(),
    fb: z.array(MystereCellSchema),
});
export const MysterePublicPlayerSchema = z.object({
    userId: z.string(),
    /** Essais joués. */
    tries: z.number().int(),
    done: z.boolean(),
    solved: z.boolean(),
    /** Ma grille : lettres + couleurs. Grille adverse : null tant que je n'ai
     *  pas fini, silhouette (couleurs sans lettres) ensuite, lettres complètes
     *  quand la partie est finie. */
    rows: z.array(MystereRowSchema).nullable(),
});
export const MysterePublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    wordLen: z.number().int(),
    maxTries: z.number().int(),
    players: z.array(MysterePublicPlayerSchema),
    /** Révélé quand phase === "finished" seulement. */
    word: z.string().nullable(),
    winnerUserId: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Morpion — tic-tac-toe ASYNCHRONE : le plus petit reducer du moteur. Grille
// 3×3, tour par tour (currentTurnUserId + push), nuls fréquents → la série
// de la conversation (GameSeriesDTO) sert de « meilleur des N ».
// ---------------------------------------------------------------------------
export const MorpionActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("place"),
        /** Case 0-8, lecture ligne par ligne. */
        cell: z.number().int().min(0).max(8),
    }),
]);
export const MorpionPublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    /** userId du propriétaire de chaque case — null si vide. */
    cells: z.array(z.string().nullable()).length(9),
    /** L'ordre de jeu : players[0] a commencé (croix côté client). */
    players: z.array(z.string()),
    currentTurnUserId: z.string().nullable(),
    winnerUserId: z.string().nullable(),
    /** Indices de la ligne gagnante — pour la surligner. */
    winningLine: z.array(z.number().int()).nullable(),
});
// ---------------------------------------------------------------------------
// Aligne 4 (roadmap #3) — mécanique puissance-4 ASYNCHRONE (⚠️ ne JAMAIS
// écrire « Puissance 4 », marque Hasbro — nom maison). Grille 7×6, gravité :
// on choisit une colonne, le jeton tombe. 4 alignés (H/V/diagonales) gagnent,
// grille pleine = nul. Tour par tour comme le Morpion (currentTurnUserId +
// push « à toi de jouer ») ; duel uniquement (bot minimax plus tard).
// ---------------------------------------------------------------------------
export const ALIGNE4_COLS = 7;
export const ALIGNE4_ROWS = 6;
export const Aligne4ActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("drop"),
        /** Colonne 0-6 — la ligne d'arrivée est calculée par le serveur (gravité). */
        col: z.number().int().min(0).max(ALIGNE4_COLS - 1),
    }),
]);
/** Aucun secret : tout le monde voit la même grille (comme le Morpion). */
export const Aligne4PublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    cols: z.number().int(),
    rows: z.number().int(),
    /** Cases ligne par ligne, rangée 0 en HAUT — userId propriétaire ou null. */
    cells: z.array(z.string().nullable()).length(ALIGNE4_COLS * ALIGNE4_ROWS),
    /** L'ordre de jeu : players[0] a commencé. */
    players: z.array(z.string()),
    currentTurnUserId: z.string().nullable(),
    /** Index du dernier jeton posé — l'animation de chute côté client. */
    lastCell: z.number().int().nullable(),
    winnerUserId: z.string().nullable(),
    /** Indices de l'alignement gagnant — pour le surligner. */
    winningCells: z.array(z.number().int()).nullable(),
});
// ---------------------------------------------------------------------------
// Les Boîtes — mécanique « boîtes & offres » ASYNCHRONE (nom maison : la
// mécanique est libre, le format télé et son présentateur ne le sont pas —
// cf. la règle juridique en tête de docs/GAMES_ROADMAP.md).
//
// 20 boîtes, une somme dans chacune. Le joueur garde la sienne, ouvre les
// autres par manches, et LE BANQUIER appelle entre chaque manche : à prendre
// (fin, gains = offre) ou à laisser.
//
// En duel, les deux joueurs reçoivent la MÊME distribution (même seed) et
// jouent chacun à leur rythme — modèle Trivia/Mot Mystère : la partie vit dans
// le chat pendant des heures. Le plus gros gain l'emporte.
// ---------------------------------------------------------------------------
/** Barème public — l'ordre est celui de l'affichage (10 petites, 10 grosses). */
export const BOITES_LADDER = [
    1, 5, 10, 50, 100, 200, 500, 1_000, 2_000, 5_000, 10_000, 15_000, 20_000,
    25_000, 30_000, 40_000, 50_000, 75_000, 100_000, 250_000,
];
export const BOITES_COUNT = BOITES_LADDER.length;
/** Boîtes à ouvrir par manche : 5+4+3+2+1+1+1+1 = 18. Il en reste alors DEUX
 *  fermées — la sienne et une autre : c'est la finale (échanger ou garder). */
export const BOITES_ROUNDS = [5, 4, 3, 2, 1, 1, 1, 1];
export const BoitesModeSchema = z.enum(["duel", "solo"]);
export const BoitesActionSchema = z.discriminatedUnion("kind", [
    /** Choisir SA boîte — première action de la partie. */
    z.object({
        kind: z.literal("pick_own"),
        box: z.number().int().min(1).max(BOITES_COUNT),
    }),
    z.object({
        kind: z.literal("open"),
        box: z.number().int().min(1).max(BOITES_COUNT),
    }),
    /** L'offre du Banquier : à prendre… */
    z.object({ kind: z.literal("deal") }),
    /** …ou à laisser. */
    z.object({ kind: z.literal("no_deal") }),
    /** Finale à deux boîtes : échanger la sienne, ou la garder. */
    z.object({ kind: z.literal("swap"), accept: z.boolean() }),
]);
export const BoitesPhaseSchema = z.enum([
    "pick_own",
    "opening",
    "offer",
    "swap",
    "finished",
]);
export const BoitesBoxSchema = z.object({
    n: z.number().int(),
    opened: z.boolean(),
    /** Le montant n'existe QUE pour une boîte ouverte — une boîte fermée ne
     *  révèle rien, y compris la sienne, y compris au dernier tour. */
    amount: z.number().int().nullable(),
    mine: z.boolean(),
});
/** Comment le parcours s'est terminé — sert la réplique finale de M. LIME. */
export const BoitesOutcomeSchema = z.enum(["deal", "kept", "swapped"]);
export const BoitesPublicPlayerSchema = z.object({
    userId: z.string(),
    /** Progression publique : nombre de boîtes ouvertes. Ne fuite aucun montant
     *  (en duel la distribution est commune, mais savoir COMBIEN l'autre a
     *  ouvert n'apprend rien sur QUOI). */
    opened: z.number().int(),
    done: z.boolean(),
    /** Convention Trivia : les gains adverses restent masqués jusqu'à la fin. */
    winnings: z.number().int().nullable(),
});
export const BoitesPublicStateSchema = z.object({
    /** MA phase : l'écran suit MON parcours. En duel, chacun avance à son
     *  rythme — je peux avoir fini quand l'autre en est à sa 3e manche. */
    phase: BoitesPhaseSchema,
    /** La PARTIE est-elle close (tous les parcours terminés) ? C'est seulement
     *  là qu'apparaissent les gains adverses et le vainqueur. */
    matchFinished: z.boolean(),
    mode: BoitesModeSchema,
    /** Barème complet — public dès le début (c'est le sel du jeu). */
    ladder: z.array(z.number().int()),
    boxes: z.array(BoitesBoxSchema),
    /** Boîtes restant à ouvrir dans la manche courante. */
    toOpen: z.number().int(),
    round: z.number().int(),
    totalRounds: z.number().int(),
    /** Offre courante du Banquier — non nulle en phase "offer" seulement. */
    offer: z.number().int().nullable(),
    offerHistory: z.array(z.number().int()),
    myBox: z.number().int().nullable(),
    /** Mes gains — posés à MA fin de parcours (pas à celle de l'adversaire). */
    winnings: z.number().int().nullable(),
    outcome: BoitesOutcomeSchema.nullable(),
    players: z.array(BoitesPublicPlayerSchema),
    winnerUserId: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Bataille navale (roadmap #4) — battleship, domaine public. ASYNCHRONE en
// DEUX phases : `placing` (chacun pose ses 5 navires à son rythme) puis
// `firing` (tir alterné → touché / coulé / raté). La REDACTION est le cœur :
// je vois MA flotte + MES tirs + les tirs adverses sur moi — la flotte
// adverse ne m'arrive JAMAIS avant la fin de partie (cas d'école du
// redact(state, forUserId), cf. GAMES_ROADMAP #4).
// ---------------------------------------------------------------------------
export const BATAILLE_GRID = 10;
/** Les 5 navires classiques : 5, 4, 3, 3, 2 cases. */
export const BATAILLE_SHIPS = [5, 4, 3, 3, 2];
export const BatailleShipSchema = z.object({
    /** Case de tête (coin haut-gauche du navire). */
    x: z.number().int().min(0).max(BATAILLE_GRID - 1),
    y: z.number().int().min(0).max(BATAILLE_GRID - 1),
    len: z.number().int().min(2).max(5),
    dir: z.enum(["h", "v"]),
});
export const BatailleActionSchema = z.discriminatedUnion("kind", [
    /** Pose TOUTE la flotte d'un coup — validée serveur (longueurs exactes,
     *  dans la grille, sans chevauchement). Une seule fois par joueur. */
    z.object({
        kind: z.literal("place_ships"),
        ships: z.array(BatailleShipSchema).length(BATAILLE_SHIPS.length),
    }),
    z.object({
        kind: z.literal("fire"),
        x: z.number().int().min(0).max(BATAILLE_GRID - 1),
        y: z.number().int().min(0).max(BATAILLE_GRID - 1),
    }),
]);
export const BatailleShotSchema = z.object({
    x: z.number().int(),
    y: z.number().int(),
    result: z.enum(["miss", "hit", "sunk"]),
});
/** État redacted POUR MOI — la flotte adverse n'y figure qu'une fois la
 *  partie FINIE (revoir le champ de bataille), jamais avant. */
export const BataillePublicStateSchema = z.object({
    phase: z.enum(["placing", "firing", "finished"]),
    grid: z.number().int(),
    shipLens: z.array(z.number().int()),
    players: z.array(z.string()),
    /** MA flotte — null tant que je ne l'ai pas posée. */
    myShips: z.array(BatailleShipSchema).nullable(),
    /** MES tirs sur la grille adverse, avec leur résultat. */
    myShots: z.array(BatailleShotSchema),
    /** Les tirs adverses sur MA grille. */
    oppShots: z.array(BatailleShotSchema),
    /** L'adversaire a posé sa flotte (sans dire où). */
    oppReady: z.boolean(),
    /** Compteurs publics — aucun placement ne fuite. */
    myShipsLeft: z.number().int(),
    oppShipsLeft: z.number().int(),
    /** Longueurs des navires adverses que j'ai coulés (affichage). */
    oppSunkLens: z.array(z.number().int()),
    /** Phase firing : à qui de tirer. Placing : le joueur qui n'a pas encore
     *  posé (si un seul manque) — pilote le push « à toi de jouer ». */
    currentTurnUserId: z.string().nullable(),
    winnerUserId: z.string().nullable(),
    /** Révélée quand phase === "finished" SEULEMENT. */
    oppShips: z.array(BatailleShipSchema).nullable(),
});
// ---------------------------------------------------------------------------
// Casse-briques (GA-1) — mécanique breakout générique, SOLO uniquement.
// Troisième jeu à TICK (100 ms) : il partage la physique balle/raquette du Pong
// (apps/api/src/services/games/physics.ts) et le socle présence/forfait.
// Le serveur est autoritaire de bout en bout — le client n'envoie qu'une
// intention de raquette et ne fait que RENDRE l'état reçu : ni la balle, ni les
// briques, ni les vies, ni le score ne se calculent côté client (anti-triche).
// Solo = jamais de GAME_WIN (règle du projet) : seul le score est conservé, il
// nourrira un futur « meilleur score entre contacts ».
// ---------------------------------------------------------------------------
export const BRIQUES_FIELD_W = 100;
export const BRIQUES_FIELD_H = 110;
export const BRIQUES_TICK_MS = 100;
/** Demi-largeur de la raquette (unités terrain). */
export const BRIQUES_PADDLE_HALF_W = 9;
/** Épaisseur de la raquette, et hauteur de sa face au-dessus du bas du terrain
 *  (la balle qui la manque tombe encore un peu avant d'être perdue). */
export const BRIQUES_PADDLE_H = 2.6;
export const BRIQUES_PADDLE_TOP = BRIQUES_FIELD_H - 8;
export const BRIQUES_BALL_R = 1.6;
/** 3 vies — la règle du genre. */
export const BRIQUES_LIVES = 3;
/** Mur : 8 colonnes, marge latérale, première rangée sous le HUD du plateau. */
export const BRIQUES_COLS = 8;
export const BRIQUES_WALL_LEFT = 4;
export const BRIQUES_WALL_TOP = 12;
export const BRIQUES_BRICK_W = (BRIQUES_FIELD_W - 2 * BRIQUES_WALL_LEFT) / BRIQUES_COLS;
export const BRIQUES_BRICK_H = 4.5;
/** Nombre de niveaux : la partie est BORNÉE (pas de tick éternel). */
export const BRIQUES_MAX_LEVEL = 5;
/** Barème : par contact de brique, puis par niveau terminé. */
export const BRIQUES_BRICK_POINTS = 10;
export const BRIQUES_LEVEL_BONUS = 100;
export const BriquesActionSchema = z.discriminatedUnion("kind", [
    /** Contrôle continu (clavier) : la raquette glisse tant que dir ≠ stop. */
    z.object({
        kind: z.literal("paddle"),
        dir: z.enum(["left", "right", "stop"]),
    }),
    /** Contrôle tactile : viser une abscisse — la vitesse reste plafonnée
     *  côté serveur (pas de téléportation sous la balle). */
    z.object({
        kind: z.literal("paddle_to"),
        x: z.number().min(0).max(BRIQUES_FIELD_W),
    }),
]);
export const BriquesEndReasonSchema = z.enum([
    /** Les 5 niveaux nettoyés. */
    "cleared",
    /** Plus de vie. */
    "no_lives",
    "forfeit",
    "waiting_timeout",
]);
export const BriquesPublicStateSchema = z.object({
    phase: z.enum(["waiting", "countdown", "playing", "finished"]),
    fieldW: z.number(),
    fieldH: z.number(),
    /** Centre de la raquette + sa géométrie (le client ne recalcule rien). */
    paddle: z.object({
        x: z.number(),
        halfW: z.number(),
        top: z.number(),
        h: z.number(),
    }),
    ball: z.object({ x: z.number(), y: z.number(), vx: z.number(), vy: z.number() }),
    /** Mur : points de vie restants par brique, en ligne (row-major), 0 =
     *  détruite. Avec `cols`/`rows` et la géométrie, le client sait tout dessiner. */
    bricks: z.array(z.number().int().min(0)),
    cols: z.number().int(),
    rows: z.number().int(),
    brickW: z.number(),
    brickH: z.number(),
    wallLeft: z.number(),
    wallTop: z.number(),
    lives: z.number().int(),
    score: z.number().int(),
    level: z.number().int(),
    maxLevel: z.number().int(),
    /** Pause de service (balle posée sur la raquette) — null hors service. */
    serveMsLeft: z.number().nullable(),
    countdownMsLeft: z.number().nullable(),
    presentUserIds: z.array(z.string()),
    winnerUserId: z.string().nullable(),
    endReason: BriquesEndReasonSchema.nullable(),
});
// Petit Bac (GA-2) — jeu traditionnel du domaine public. PREMIER jeu à MANCHES :
// le serveur tire une lettre par manche, les catégories sont fixes pour toute la
// partie, chacun remplit sa copie à son rythme puis « rend sa copie ». Quand
// tout le monde a rendu (ou que la durée maximale de la manche est écoulée), la
// manche est JUGÉE : tout est révélé, le barème s'applique, la manche suivante
// s'ouvre sur une nouvelle lettre.
//
// REDACTION : la lettre de la manche en cours est publique (on écrit tous
// dessus), mais les mots des AUTRES joueurs restent invisibles jusqu'à la
// clôture de la manche — c'est tout l'intérêt du jeu. Les lettres des manches à
// venir ne sortent jamais de l'état redacted.
//
// Les catégories circulent en CLÉS stables (`prenom`, `animal`…) : le client les
// traduit (i18n fr/en) et tolère une clé inconnue (client N-1).
// ---------------------------------------------------------------------------
/** Nombre de manches d'une partie. */
export const PETITBAC_ROUNDS = 3;
/** Nombre de catégories tirées à l'init, fixes pour toute la partie. */
export const PETITBAC_CATEGORY_COUNT = 5;
/**
 * Durée maximale d'une manche POUR UN JOUEUR — le compte à rebours de CHACUN
 * part à SON premier coup de la manche. Un chrono personnel : mon adversaire ne
 * peut pas déclencher le mien en écrivant de son côté (correctif de revue).
 */
export const PETITBAC_ROUND_MS = 3 * 60_000;
/**
 * Fenêtre laissée à un joueur qui n'a pas TOUCHÉ la manche (app fermée) avant
 * que l'autre puisse la clore sans lui. C'est le prix de l'asynchrone : sans
 * elle, un joueur seul déroulerait la partie entière en 10 minutes pendant que
 * l'autre est dans le métro. Le joueur absent est prévenu par push à
 * l'ouverture de la manche — il a donc une journée pour répondre.
 */
export const PETITBAC_ROUND_ABSENT_MS = 24 * 3600_000;
/** Nombre maximal de cases portées par un « je rends ma copie ». */
export const PETITBAC_MAX_CATEGORIES = 8;
/** Longueur maximale d'un mot (minimisation : c'est une case de jeu, pas un
 *  champ libre). */
export const PETITBAC_WORD_MAX = 24;
/** Barème par manche : mot valide et unique / valide mais partagé. */
export const PETITBAC_POINTS_UNIQUE = 2;
export const PETITBAC_POINTS_SHARED = 1;
/**
 * TOUTE action porte la MANCHE qu'elle visait (`round`). Sans ce numéro, une
 * action émise juste avant la clôture — un mot en vol, un « je rends » cliqué
 * pendant la bascule — atterrirait dans la manche SUIVANTE, avec la mauvaise
 * lettre. Avec lui, le serveur la reconnaît comme périmée et n'en fait rien.
 * C'est aussi ce qui rend les actions idempotentes (§12 : recevoir deux fois le
 * même événement ne doit rien casser).
 */
export const PetitBacActionSchema = z.discriminatedUnion("kind", [
    /** Écrire (ou corriger) une case. Envoyé au fil de la frappe côté client, mais
     *  JAMAIS rediffusé aux autres : voir la redaction. Mot vide = case effacée. */
    z.object({
        kind: z.literal("word"),
        round: z.number().int().min(1),
        category: z.string().min(1).max(40),
        word: z.string().max(PETITBAC_WORD_MAX),
    }),
    /**
     * « Je rends ma copie » — clôt MA participation à la manche, pas celle des
     * autres. Irréversible pour la manche en cours.
     *
     * `words` PORTE LA COPIE : le rendu écrit les cases puis rend, en UNE SEULE
     * frame. C'est le correctif d'un défaut de revue — le client envoyait
     * « la case en attente » puis « je rends » dans le même tick JS, et le verrou
     * par match du moteur (SET NX) répondait BUSY à la seconde action puis
     * l'ABANDONNAIT : le rendu était perdu, ou pire, les cases tapées juste avant.
     * Rien ne sérialise deux frames d'un même socket (le handler de message n'est
     * pas attendu) : la seule garantie possible est de n'en émettre qu'une.
     * Absent = rendu sans réécrire les cases (compatible client N-1).
     */
    z.object({
        kind: z.literal("submit"),
        round: z.number().int().min(1),
        words: z
            .record(z.string().min(1).max(40), z.string().max(PETITBAC_WORD_MAX))
            .refine((w) => Object.keys(w).length <= PETITBAC_MAX_CATEGORIES, {
            message: "trop de cases",
        })
            .optional(),
    }),
    /** Durée maximale de manche écoulée → clôture et jugement. Refusé avant
     *  l'échéance : c'est le serveur qui tranche, pas le client. */
    z.object({
        kind: z.literal("close_round"),
        round: z.number().int().min(1),
    }),
]);
/** Une case jugée, telle qu'elle apparaît au récapitulatif de manche. */
export const PetitBacJudgedWordSchema = z.object({
    category: z.string(),
    /** Mot tel qu'il a été saisi (vide = case laissée blanche). */
    word: z.string(),
    /** v1 : commence par la lettre et non vide. */
    valid: z.boolean(),
    /** Même mot (forme normalisée) qu'un autre joueur dans cette catégorie. */
    shared: z.boolean(),
    points: z.number().int(),
});
/** Manche CLOSE — entièrement publique (c'est le récapitulatif du jeu). */
export const PetitBacRoundResultSchema = z.object({
    /** 1-based. */
    index: z.number().int(),
    letter: z.string(),
    copies: z.array(z.object({
        userId: z.string(),
        words: z.array(PetitBacJudgedWordSchema),
        /** Points marqués sur CETTE manche. */
        score: z.number().int(),
    })),
});
export const PetitBacPublicPlayerSchema = z.object({
    userId: z.string(),
    /** A rendu sa copie pour la manche en cours. */
    submitted: z.boolean(),
    /** Cases remplies dans la manche en cours — progression PUBLIQUE, jamais le
     *  contenu (savoir que l'autre a rempli 3 cases n'apprend aucun mot). */
    filled: z.number().int(),
    /** Score cumulé des manches CLOSES (donc déjà publiques). */
    score: z.number().int(),
    /** Les mots de la manche EN COURS : renseignés pour MOI seulement, `null`
     *  pour les autres tant que la manche n'est pas jugée. */
    words: z.record(z.string()).nullable(),
});
export const PetitBacPublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    /** Manche en cours, 1-based. */
    round: z.number().int(),
    totalRounds: z.number().int(),
    /** Clés de catégories, fixes pour toute la partie. */
    categories: z.array(z.string()),
    /** Lettre de la manche EN COURS — publique dès son tirage, jamais avant. */
    letter: z.string(),
    /** MON échéance personnelle (epoch ms) — `null` tant que JE n'ai pas joué.
     *  Le coup de l'adversaire ne démarre plus mon chrono. */
    roundEndsAt: z.number().int().nullable(),
    /** Instant à partir duquel la manche PEUT être close (tous les chronos
     *  personnels des joueurs restants ont expiré). `null` = plus personne à
     *  attendre. Le serveur revérifie l'heure : ce champ ne pilote que l'UI. */
    roundClosableAt: z.number().int().nullable(),
    roundMs: z.number().int(),
    /** Mode de validation — v1 « auto » ; v2 « vote » (non livré). */
    validation: z.enum(["auto", "vote"]),
    players: z.array(PetitBacPublicPlayerSchema),
    /** Manches déjà jugées, dans l'ordre. */
    history: z.array(PetitBacRoundResultSchema),
    /** Le joueur qu'on attend (un seul n'a pas rendu) — pilote le push. */
    currentTurnUserId: z.string().nullable(),
    winnerUserId: z.string().nullable(),
});
// « Papillon », manche bleue (GA-4) — jeu de cartes SOUS LICENCE de l'éditeur
// (Henri la détient) : les noms officiels sont employés, aucun asset ne l'est.
// TOUTE la configuration des cartes (noms, paquet, barème) vit dans
// `papillonCards.ts`, ré-exporté en bas de ce fichier.
//
// ASYNCHRONE (modèle Pendu) : un tapis de cartes au centre, une main par
// joueur, on DOIT jouer une carte à son tour. Trois ramassages — égalité,
// somme (« cumuler »), opération (« combiner », consomme un atout `−` `×` `÷`).
// Aucun ramassage = défausse sur le tapis.
//
// L'ARITHMÉTIQUE EST REVALIDÉE PAR LE SERVEUR, TOUJOURS : le client ne calcule
// que l'aperçu affiché, et un coup impossible est refusé SANS consommer la
// carte. Redaction : ma main en clair, la main adverse en NOMBRE de cartes,
// tapis et défausse publics, la PIOCHE ne sort jamais de l'état redacted.
// ---------------------------------------------------------------------------
/** Une carte. `value` n'existe que pour `num`, `op` que pour les atouts. */
export const PapillonCardSchema = z.object({
    /** Identifiant stable dans la partie (largeur fixe : `k00`…). */
    id: z.string().min(1).max(24),
    kind: PapillonCardKindSchema,
    value: z.number().int().min(PAPILLON_MIN_VALUE).max(PAPILLON_MAX_VALUE).nullable(),
    op: PapillonOpSchema.nullable(),
});
/** La cible du coup : les cartes du tapis visées, et l'opération choisie.
 *  - `op` absent : 1 carte de même valeur (égalité) ou 2+ cartes en somme ;
 *  - `op: "+"`   : la somme, écrite explicitement (gratuite) ;
 *  - `-` `*` `/` : consomme un atout de cet opérateur dans ma main. */
export const PapillonTargetSchema = z.object({
    captureIds: z.array(z.string().min(1).max(24)).max(12),
    op: PapillonOpSchema.optional(),
});
export const PapillonActionSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("play"),
        cardId: z.string().min(1).max(24),
        /** Absent = défausse pure (la carte rejoint le tapis). */
        target: PapillonTargetSchema.optional(),
    }),
]);
/** Comment le dernier coup s'est résolu — public, pour la mise en scène. */
export const PapillonPlayKindSchema = z.enum([
    "equal",
    "sum",
    "op",
    "rafle",
    "discard",
]);
export const PapillonLastPlaySchema = z.object({
    userId: z.string(),
    card: PapillonCardSchema,
    kind: PapillonPlayKindSchema,
    captured: z.array(PapillonCardSchema),
    op: PapillonOpSchema.nullable(),
    /** Valeur prise par le Couteau Suisse sur ce coup (null sinon). */
    jokerValue: z.number().int().nullable(),
    /** Sens du jeu après le coup (Chrysalide / Sens). */
    dir: z.union([z.literal(1), z.literal(-1)]),
});
/** Vue publique d'un joueur : jamais ses cartes, seulement des compteurs (les
 *  prises se font à vue — le nombre de spéciales ramassées est public). */
export const PapillonPublicPlayerSchema = z.object({
    userId: z.string(),
    handCount: z.number().int(),
    capturedCount: z.number().int(),
    specialsCount: z.number().int(),
    /** Points acquis à cet instant (spéciales × 10) — hors bonus de fin. */
    points: z.number().int(),
    /** Adversaire scripté du mode solo. */
    bot: z.boolean(),
});
/** Décompte de fin de manche, détaillé — c'est lui qui rend la règle du zéro
 *  point compréhensible à l'écran (sinon elle passe pour un bug). */
export const PapillonScoreLineSchema = z.object({
    userId: z.string(),
    specials: z.number().int(),
    cards: z.number().int(),
    specialPoints: z.number().int(),
    mostCardsBonus: z.number().int(),
    /** Règle du zéro point : +10 par adversaire si zéro, −10 par adversaire à
     *  zéro. Peut être négatif. */
    zeroRule: z.number().int(),
    total: z.number().int(),
});
export const PapillonPublicStateSchema = z.object({
    phase: z.enum(["playing", "finished"]),
    mode: z.enum(["duel", "solo"]),
    /** MA main, en clair. Celle des autres n'est QU'un compteur (players). */
    myHand: z.array(PapillonCardSchema),
    tapis: z.array(PapillonCardSchema),
    /** Défausse publique : les atouts d'opération dépensés (hors jeu, 0 point). */
    discard: z.array(PapillonCardSchema),
    /** Cartes restant à la pioche — son CONTENU est un secret serveur. */
    pileCount: z.number().int(),
    players: z.array(PapillonPublicPlayerSchema),
    /** Sens du jeu : 1 = ordre des joueurs, −1 = inversé (Chrysalide). */
    dir: z.union([z.literal(1), z.literal(-1)]),
    currentTurnUserId: z.string().nullable(),
    lastPlay: PapillonLastPlaySchema.nullable(),
    /** Présent en phase `finished` seulement. */
    scores: z.array(PapillonScoreLineSchema).nullable(),
    winnerUserId: z.string().nullable(),
});
// ---------------------------------------------------------------------------
// Carte Défi vivante — aperçu générique (jeux post-Pendu). Le Pendu garde son
// champ `preview` historique ; les nouveaux jeux exposent `livePreview`,
// structuré (le client compose le texte, i18n oblige). Jamais de secret.
// ---------------------------------------------------------------------------
export const GameLivePreviewSchema = z.object({
    phase: z.string(),
    /** Scores en cours (Snake : pommes, Pong : points, Trivia : bonnes réponses). */
    scores: z
        .array(z.object({ userId: z.string(), score: z.number().int() }))
        .optional(),
    /** Progression (Trivia : questions répondues / total). */
    progress: z
        .array(z.object({
        userId: z.string(),
        done: z.number().int(),
        total: z.number().int(),
    }))
        .optional(),
    /** Jeux tour par tour : à qui de jouer. */
    currentTurnUserId: z.string().nullable().optional(),
    /** Morpion : la grille EST l'aperçu — 9 caractères "X"/"O"/"·", parfaite en
     *  monospace dans la carte Défi. Additif, jamais de secret. */
    board: z.string().optional(),
    /** Largeur de la grille `board` (Aligne 4 : 7) — absent = 3 (Morpion). */
    boardW: z.number().int().optional(),
});
// ---------------------------------------------------------------------------
// REST — création de match + DTO
// ---------------------------------------------------------------------------
export const CreateMatchInputSchema = z.object({
    gameType: GameTypeSchema,
    sessionId: z.string().uuid(),
    /** Pendu : "duel" (défaut) ou "chosen" (mot imposé, `word` requis). */
    mode: PenduModeSchema.optional(),
    /** Mode "chosen" : le mot choisi par le challenger — JAMAIS renvoyé en DTO. */
    word: z.string().trim().min(2).max(24).optional(),
    config: z.record(z.unknown()).optional(),
});
/** Partie solo (hub) : ACTIVE immédiatement, sans session ni adversaire. */
export const CreateSoloMatchInputSchema = z.object({
    gameType: GameTypeSchema,
});
export const GameMatchParticipantDTOSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    score: z.number().int(),
    placement: z.number().int().nullable(),
    pointsAwarded: z.number().int(),
});
/** Série de manches d'une session (parties FINISHED cumulées) — « Henri 3 · Léa 2 ». */
export const GameSeriesDTOSchema = z.object({
    total: z.number().int(),
    tally: z.array(z.object({
        userId: z.string(),
        displayName: z.string(),
        wins: z.number().int(),
    })),
});
export const GameMatchDTOSchema = z.object({
    id: z.string(),
    gameType: GameTypeSchema,
    status: GameMatchStatusSchema,
    /** Mode de jeu (Pendu : duel/chosen/solo) — le mot, lui, ne sort jamais. */
    mode: z.string().nullable().optional(),
    /** Aperçu public de la partie ACTIVE pour la carte Défi (asynchrone :
     *  la partie vit dans le chat, on n'ouvre le plateau que pour jouer).
     *  Redacted pour le demandeur — aucun secret dedans. */
    preview: z
        .object({
        masked: z.string(),
        errors: z.number().int(),
        maxErrors: z.number().int(),
        currentTurnUserId: z.string().nullable(),
    })
        .nullable()
        .optional(),
    /** Aperçu générique de la partie ACTIVE (jeux post-Pendu) — voir
     *  GameLivePreviewSchema. Structuré : le client compose le texte. */
    livePreview: GameLivePreviewSchema.nullable().optional(),
    sessionId: z.string().nullable(),
    groupId: z.string().nullable(),
    createdById: z.string(),
    winnerUserId: z.string().nullable(),
    createdAt: z.string(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    participants: z.array(GameMatchParticipantDTOSchema),
});
// ---------------------------------------------------------------------------
// GA-4 — les constantes des cartes Papillon (noms OFFICIELS, composition du
// paquet, barème, emplacement de la mention de crédit de l'éditeur) sont
// ré-exportées ici : client et serveur les obtiennent depuis
// `@livemessage/protocol` sans nouvelle entrée dans `index.ts`.
// ---------------------------------------------------------------------------
export * from "./papillonCards.js";
//# sourceMappingURL=games.js.map